import { Check, Play } from "lucide-react";

import { cn } from "@/utils/cn";

export type StepStatus = "done" | "current" | "future";

export interface ProgressStep {
  number: number;
  label: string;
  subLabel: string;
  status: StepStatus;
}

const STATUS_CIRCLE: Record<StepStatus, string> = {
  done:    "bg-success text-success-foreground",
  current: "bg-warning text-warning-foreground",
  future:  "bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<StepStatus, string> = {
  done:    "text-foreground font-semibold",
  current: "text-warning font-semibold",
  future:  "text-muted-foreground font-medium",
};

const Connector = ({ done }: { done: boolean }) => (
  <div className="flex-1 flex items-center justify-center px-3 relative">
    <div className="h-px w-full bg-border" />
    {done && (
      <Play size={16} className="text-success fill-success absolute bg-background" />
    )}
  </div>
);

export const ProgressStepper = ({ steps }: { steps: ProgressStep[] }) => (
  <div className="flex items-center gap-0">
    {steps.map((s, i) => (
      <div key={s.number} className="flex items-center flex-1">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0 size-8">
            {s.status === "current" && (
              <span className="absolute inset-0 rounded-full bg-warning opacity-75 animate-ping [animation-duration:2s]" />
            )}
            <div
              className={cn(
                "relative size-8 rounded-full flex items-center justify-center text-xs font-bold",
                STATUS_CIRCLE[s.status],
              )}
            >
              {s.status === "future" ? s.number : <Check size={16} />}
            </div>
          </div>
          <div className="flex flex-col min-w-0">
            <span className={cn("text-sm leading-tight truncate", STATUS_LABEL[s.status])}>{s.label}</span>
            <span className="text-[11px] text-gray-400 leading-tight truncate">{s.subLabel}</span>
          </div>
        </div>
        {i < steps.length - 1 && (
          <Connector done={s.status !== "future"} />
        )}
      </div>
    ))}
  </div>
);
